<template>
  <div class="billing-container">
    <div class="billing-header">
      <h1>Kredit alapú fizetés</h1>
      <p>Egyenleg (HUF), havi díjterv, kedvezmények és távollétek kezelése</p>
    </div>

    <div v-if="error" class="error-message">{{ error }}</div>

    <!-- Wallet card -->
    <div class="wallet-card" :class="{ 'in-debt': wallet && wallet.balance < 0 }">
      <div class="wallet-left">
        <span class="wallet-label">Egyenleg</span>
        <span class="wallet-balance">{{ formatPrice(wallet ? wallet.balance : 0) }}</span>
        <span class="wallet-currency">{{ wallet ? wallet.currency : 'HUF' }}</span>
      </div>
      <div class="wallet-actions" v-if="canCredit">
        <button class="btn-primary" @click="openTopUp">Feltöltés</button>
        <button class="btn-secondary" @click="openCorrect">Korrekció</button>
        <button class="btn-secondary" @click="openRefund">Visszatérítés</button>
      </div>
    </div>

    <!-- Group base prices (catalog) -->
    <div class="section">
      <div class="section-header">
        <h2>Csoport alapárak</h2>
      </div>
      <div v-if="groupPrices.length > 0" class="group-price-table-wrap">
        <table class="group-price-table">
          <thead>
            <tr><th>Csoport</th><th>Óra ára</th><th>Havi ár</th></tr>
          </thead>
          <tbody>
            <tr v-for="gp in groupPrices" :key="gp.group_id">
              <td class="group-name">{{ groupName(gp.group_id) }}</td>
              <td>{{ formatPrice(gp.price_per_class) }}</td>
              <td>{{ formatPrice(gp.price_monthly) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="empty-note">Még nincs beállítva csoport ár.</div>
    </div>

    <!-- Custom pricing factors (competitor / multiple groups) -->
    <div class="section">
      <div class="section-header">
        <h2>Egyedi árazás (tényezők)</h2>
        <button v-if="canSetFactors" class="btn-primary" @click="openFactorModal">Beállítás</button>
      </div>
      <p class="section-hint">A gyerek egyedi ára tényezők alapján alakul: versenyzői státusz és több csoportos részvétel automatikusan kedvezményt ad (ha van hozzá szabály).</p>
      <div class="factor-row">
        <span class="factor-badge" :class="{ active: childInfo && childInfo.is_competitor }">
          Versenyző: {{ (childInfo && childInfo.is_competitor) ? 'Igen' : 'Nem' }}
        </span>
        <span class="factor-badge" :class="{ active: hasMultipleGroups }">
          Több csoport: {{ hasMultipleGroups ? 'Igen (' + activePlanCount + ')' : 'Nem' }}
        </span>
        <span v-if="factorDiscountApplied > 0" class="factor-discount">
          Érvényes tényező-kedvezmény: -{{ formatPrice(factorDiscountApplied) }}
        </span>
      </div>
      <div v-if="factorDiscounts.length === 0" class="empty-note">Nincs aktív tényező-kedvezmény. Adj hozzá szabályt a Beállítások &rarr; Fizetési beállításoknál.</div>
    </div>

      <!-- Monthly calculation -->
      <div class="section">
        <div class="section-header">
          <h2>Havi számla</h2>
          <div class="month-picker">
            <input type="month" v-model="selectedMonth" @change="loadCalculation" />
          </div>
        </div>

        <div v-if="calculation" class="calc-summary">
          <div class="calc-row"><span>Alapösszeg:</span><span>{{ formatPrice(calcBase) }}</span></div>
          <div class="calc-row discount"><span>Kedvezmények:</span><span>-{{ formatPrice(calcDiscount) }}</span></div>
          <div class="calc-row absence"><span>Távollét csökkentés:</span><span>-{{ formatPrice(calcAbsence) }}</span></div>
          <div class="calc-row total"><span>Végösszeg:</span><span>{{ formatPrice(calculation.total) }}</span></div>
        </div>

        <div v-if="canWrite" class="override-row">
          <label>Dancer havi díja (HUF) — admin felülírása:</label>
          <input type="number" v-model.number="overrideInput" :placeholder="'Számolt: ' + (calculation ? formatPrice(calculation.total) : '?')" />
          <button class="btn-primary" @click="saveOverride">Mentés</button>
          <button class="btn-secondary" v-if="overrideSet" @click="clearOverride">Számított használata</button>
        </div>

      <div v-for="cp in calculation ? calculation.plans : []" :key="cp.plan.id" class="plan-calc">
        <div class="plan-calc-head">
          <strong>{{ cp.plan.group_name }}</strong>
          <span class="mode-tag">{{ cp.plan.billing_mode === 'monthly' ? 'Havi' : 'Óra' }}</span>
          <span class="plan-total">{{ formatPrice(cp.monthly_total) }}</span>
        </div>
        <div class="plan-calc-body">
          <template v-if="cp.plan.billing_mode === 'monthly' && cp.lessons_in_month > 0">
            <span>Havi díj: {{ formatPrice(cp.catalog_base) }}</span>
            <span class="per-lesson">Óránként: {{ formatPrice(cp.per_lesson) }} ({{ cp.attended }}/{{ cp.lessons_in_month }} óra)</span>
            <span>Alap (teljesítve): {{ formatPrice(cp.base_price) }}</span>
          </template>
          <span v-else>Alap: {{ formatPrice(cp.base_price) }}</span>
          <span v-for="d in cp.discounts" :key="d.rule_id" class="applied-discount">
            {{ d.rule_name }}: -{{ formatPrice(d.applied) }}
          </span>
          <span v-if="cp.absence_reduction > 0" class="applied-absence">
            Távollét: -{{ formatPrice(cp.absence_reduction) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Billing plans -->
    <div class="section">
      <div class="section-header">
        <h2>Díjtervek (csoportonként)</h2>
        <button v-if="canWrite" class="btn-primary" @click="openPlanModal">+ Terv</button>
      </div>
      <div v-for="plan in plans" :key="plan.id" class="plan-card">
        <div class="plan-head">
          <strong>{{ plan.group_name }}</strong>
          <span class="mode-tag">{{ plan.billing_mode === 'monthly' ? 'Havi' : 'Óra' }}</span>
          <span v-if="plan.override_price != null" class="override-tag">Egyedi ár (gyerek): {{ formatPrice(plan.override_price) }}</span>
          <span v-else class="catalog-tag">Katalógus ár</span>
          <span v-if="!plan.is_active" class="inactive-tag">Inaktív</span>
          <div class="plan-actions" v-if="canSetFactors">
            <button class="btn-edit" @click="editPlan(plan)">Szerk</button>
            <button class="btn-delete" @click="removePlan(plan.id)">Töröl</button>
          </div>
        </div>
        <div class="plan-discounts">
          <span v-for="d in plan.discounts" :key="d.id" class="discount-chip" :class="'scope-' + (d.scope || 'custom')">
            {{ d.rule_name }} ({{ scopeText(d.scope) }})
            <em v-if="d.override_value != null">(egyedi: {{ formatPrice(d.override_value) }})</em>
            <button v-if="canSetFactors" class="chip-x" @click="removePlanDiscount(plan.id, d.rule_id)">×</button>
          </span>
          <button v-if="canSetFactors" class="btn-add-discount" @click="openAddDiscount(plan)">+ Egyedi kedvezmény</button>
        </div>
      </div>
      <div v-if="plans.length === 0" class="empty-note">Még nincs díjterv.</div>
    </div>

    <!-- Absences -->
    <div class="section">
      <div class="section-header">
        <h2>Távollétek (utazás, kimaradás)</h2>
        <button v-if="canAbsence" class="btn-primary" @click="openAbsenceModal">+ Távollét</button>
      </div>
      <div v-for="a in absences" :key="a.id" class="absence-card">
        <div>
          <strong>{{ a.start_date }} → {{ a.end_date }}</strong>
          <span class="absence-kind"> ({{ absenceKindText(a.reduction_kind) }}: {{ formatPrice(a.reduction_value) }})</span>
        </div>
        <div class="absence-actions" v-if="canAbsence">
          <button class="btn-delete" @click="removeAbsence(a.id)">Töröl</button>
        </div>
      </div>
      <div v-if="absences.length === 0" class="empty-note">Nincs rögzített távollét.</div>
    </div>

    <!-- Ledger -->
    <div class="section">
      <div class="section-header"><h2>Egyenlegmozgások</h2></div>
      <table class="ledger-table">
        <thead>
          <tr><th>Dátum</th><th>Típus</th><th>Összeg</th><th>Egyenleg</th><th>Ok</th><th>Megjegyzés</th></tr>
        </thead>
        <tbody>
          <tr v-for="e in ledger" :key="e.id">
            <td>{{ formatDateTime(e.created_at) }}</td>
            <td>{{ txnText(e.txn_type) }}</td>
            <td :class="e.amount < 0 ? 'neg' : 'pos'">{{ formatPrice(e.amount) }}</td>
            <td>{{ formatPrice(e.balance_after) }}</td>
            <td>{{ e.reason }}</td>
            <td>{{ e.note }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="ledger.length === 0" class="empty-note">Nincs mozgás.</div>
    </div>

    <!-- Plan modal -->
    <div v-if="showPlanModal" class="modal-overlay" @click="showPlanModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ editingPlan ? 'Terv szerkesztése' : 'Új díjterv' }}</h3>
          <button @click="showPlanModal = false" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Csoport</label>
            <select v-model="planForm.group_id" class="modern-select">
              <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Számlázás</label>
            <select v-model="planForm.billing_mode" class="modern-select">
              <option value="monthly">Havi (fix ár)</option>
              <option value="class">Óránként (mindig fizet)</option>
            </select>
          </div>
          <div class="form-group">
            <label>Egyedi ár (HUF) - üres = katalógus ár</label>
            <input type="number" v-model="planForm.override_price" placeholder="pl. 12000" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Megjegyzés</label>
            <input type="text" v-model="planForm.note" class="modern-input" />
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="planForm.is_active" /> Aktív
            </label>
          </div>
          <div class="form-actions">
            <button class="btn-secondary" @click="showPlanModal = false">Mégse</button>
            <button class="btn-primary" @click="savePlan">Mentés</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add discount to plan modal -->
    <div v-if="showDiscountModal" class="modal-overlay" @click="showDiscountModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Kedvezmény hozzáadása: {{ discountTargetPlan.group_name }}</h3>
          <button @click="showDiscountModal = false" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Szabály</label>
            <select v-model="discountForm.rule_id" class="modern-select">
              <option v-for="r in discountRules" :key="r.id" :value="r.id">
                {{ r.name }} ({{ discountKindText(r.kind) }} {{ formatPrice(r.value) }} / {{ r.scope }})
              </option>
            </select>
          </div>
          <div class="form-group">
            <label>Egyedi érték (HUF vagy %) - üres = szabály alapértelmezett</label>
            <input type="number" v-model="discountForm.override_value" placeholder="opcionális" class="modern-input" />
          </div>
          <div class="form-actions">
            <button class="btn-secondary" @click="showDiscountModal = false">Mégse</button>
            <button class="btn-primary" @click="savePlanDiscount">Hozzáadás</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Absence modal -->
    <div v-if="showAbsenceModal" class="modal-overlay" @click="showAbsenceModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Új távollét</h3>
          <button @click="showAbsenceModal = false" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Kezdete</label>
            <input type="date" v-model="absenceForm.start_date" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Vége</label>
            <input type="date" v-model="absenceForm.end_date" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Csökkentés típusa</label>
            <select v-model="absenceForm.reduction_kind" class="modern-select">
              <option value="proportional">Arányos (hónap hátralévő része)</option>
              <option value="fixed">Fix összeg (HUF)</option>
              <option value="percent">Százalék (%)</option>
            </select>
          </div>
          <div class="form-group">
            <label>Érték (0-1 arányos, HUF fix, 0-100 százalék)</label>
            <input type="number" step="0.01" v-model="absenceForm.reduction_value" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Ok</label>
            <input type="text" v-model="absenceForm.reason" placeholder="pl. külföldi utazás" class="modern-input" />
          </div>
          <div class="form-actions">
            <button class="btn-secondary" @click="showAbsenceModal = false">Mégse</button>
            <button class="btn-primary" @click="saveAbsence">Mentés</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Factor modal (competitor / multiple groups) -->
    <div v-if="showFactorModal" class="modal-overlay" @click="showFactorModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Egyedi árazás tényezők</h3>
          <button @click="showFactorModal = false" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" v-model="factorForm.is_competitor" /> Versenyző (automatikusan alkalmazza a "versenyző" kedvezményszabályt)
            </label>
          </div>
          <p class="section-hint">Több csoportos részvétel esetén a "többcsoportos" kedvezményszabály automatikusan érvényesül, ha van hozzá szabály a Fizetési beállításokban.</p>
          <div class="form-actions">
            <button class="btn-secondary" @click="showFactorModal = false">Mégse</button>
            <button class="btn-primary" @click="saveFactor" :disabled="savingFactor">
              {{ savingFactor ? 'Mentés...' : 'Mentés' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Wallet action modal -->
    <div v-if="showWalletModal" class="modal-overlay" @click="showWalletModal = false">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ walletAction.title }}</h3>
          <button @click="showWalletModal = false" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label>Összeg (HUF)</label>
            <input type="number" v-model="walletForm.amount" class="modern-input" />
          </div>
          <div class="form-group">
            <label>Megjegyzés</label>
            <input type="text" v-model="walletForm.note" class="modern-input" />
          </div>
          <div class="form-actions">
            <button class="btn-secondary" @click="showWalletModal = false">Mégse</button>
            <button class="btn-primary" @click="submitWalletAction">Mentés</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'BillingItem',
  props: {
    childId: { type: [Number, String], default: null }
  },
  data() {
    return {
      activeChildId: null,
      wallet: null,
      ledger: [],
      plans: [],
      absences: [],
      discountRules: [],
      groups: [],
      groupPrices: [],
      calculation: null,
      selectedMonth: this.currentMonth(),
      error: '',
      canWrite: false,
      canCredit: false,
      canAbsence: false,
      canSetFactors: false,
      childInfo: { is_competitor: false },
      overrideInput: null,
      overrideSet: false,
      showFactorModal: false,
      factorForm: { is_competitor: false },
      savingFactor: false,
      showPlanModal: false,
      editingPlan: null,
      planForm: { group_id: null, billing_mode: 'monthly', override_price: '', note: '', is_active: true },
      showDiscountModal: false,
      discountTargetPlan: null,
      discountForm: { rule_id: null, override_value: '' },
      showAbsenceModal: false,
      absenceForm: { start_date: '', end_date: '', reduction_kind: 'proportional', reduction_value: 0.5, reason: '' },
      showWalletModal: false,
      walletAction: { type: 'topup', title: '' },
      walletForm: { amount: 0, note: '', method: 'készpénz' }
    };
  },
  computed: {
    calcBase() {
      if (!this.calculation || !this.calculation.plans) return 0;
      return this.calculation.plans.reduce((s, p) => s + p.base_price, 0);
    },
    calcDiscount() {
      if (!this.calculation || !this.calculation.plans) return 0;
      return this.calculation.plans.reduce((s, p) => s + (p.discounts || []).reduce((a, d) => a + (d.applied || 0), 0), 0);
    },
    calcAbsence() {
      if (!this.calculation || !this.calculation.plans) return 0;
      return this.calculation.plans.reduce((s, p) => s + p.absence_reduction, 0);
    },
    activePlanCount() {
      return (this.plans || []).filter(p => p.is_active).length;
    },
    hasMultipleGroups() {
      return this.activePlanCount > 1;
    },
    // Factor-based (auto) discounts currently applied, derived live from the monthly bill.
    factorDiscounts() {
      const out = [];
      if (this.childInfo && this.childInfo.is_competitor) {
        out.push({ key: 'competitor', label: 'Versenyző', scope: 'competitor' });
      }
      if (this.hasMultipleGroups) {
        out.push({ key: 'multi_group', label: 'Több csoport', scope: 'multi_group' });
      }
      return out;
    },
    // Sum of applied factor discounts actually reflected in the live monthly bill.
    factorDiscountApplied() {
      if (!this.calculation || !this.calculation.plans) return 0;
      let sum = 0;
      this.calculation.plans.forEach(p => {
        (p.discounts || []).forEach(d => {
          if (this.factorDiscounts.some(f => f.scope === d.scope)) sum += (d.applied || 0);
        });
      });
      return sum;
    }
  },
  async mounted() {
    const id = this.childId || this.$route.params.id || this.$route.params.childId;
    this.activeChildId = parseInt(id, 10);
    this.refreshPermissions();
    await this.loadAll();
  },
  methods: {
    // Permissions: prefer localStorage (always set on login, the source that
    // reliably works at mount time) and fall back to the reactive store getters.
    refreshPermissions() {
      const isAdmin = localStorage.getItem('isAdmin') === 'true' || this.$isAdmin() === true;
      const isTeacher = localStorage.getItem('isTeacher') === 'true' || this.$isTeacher() === true;
      const isAssistant = localStorage.getItem('isAssistant') === 'true' || this.$isAssistant() === true;
      this.canWrite = isAdmin || isAssistant;
      this.canCredit = isAdmin || isTeacher;
      this.canAbsence = isAdmin || isTeacher;
      this.canSetFactors = isAdmin || isTeacher;
    },
    currentMonth() {
      const d = new Date();
      return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0');
    },
    async loadAll() {
      await Promise.all([
        this.loadWallet(),
        this.loadPlans(),
        this.loadAbsences(),
        this.loadLedger(),
        this.loadMeta(),
        this.loadCalculation(),
        this.loadGroupPrices(),
        this.loadChildInfo(),
        this.loadOverride()
      ]);
    },
    async loadOverride() {
      try {
        const r = await axios.get(`${this.api()}/billing/override/${this.activeChildId}`);
        if (r.data.override !== null && r.data.override !== undefined) {
          this.overrideInput = r.data.override;
          this.overrideSet = true;
        } else {
          this.overrideInput = null;
          this.overrideSet = false;
        }
      } catch (e) { /* ignore */ }
    },
    async saveOverride() {
      const v = this.overrideInput === '' || this.overrideInput === null ? null : parseFloat(this.overrideInput);
      try {
        await axios.put(`${this.api()}/billing/override`, { child_id: this.activeChildId, value: v });
        this.overrideSet = (v !== null && !isNaN(v));
        await this.refreshBilling();
      } catch (e) { this.error = 'Hiba a felülírás mentésekor'; }
    },
    async clearOverride() {
      this.overrideInput = null;
      try {
        await axios.put(`${this.api()}/billing/override`, { child_id: this.activeChildId, value: null });
        this.overrideSet = false;
        await this.refreshBilling();
      } catch (e) { this.error = 'Hiba a felülírás törlésekor'; }
    },
    async loadChildInfo() {
      try {
        const r = await axios.get(`${this.api()}/children/${this.activeChildId}/competitor`);
        this.childInfo = { is_competitor: !!(r.data && r.data.is_competitor) };
      } catch (e) { /* ignore */ }
    },
    openFactorModal() {
      this.factorForm = { is_competitor: !!(this.childInfo && this.childInfo.is_competitor) };
      this.showFactorModal = true;
    },
    async saveFactor() {
      this.savingFactor = true;
      try {
        await axios.put(`${this.api()}/children/${this.activeChildId}/competitor`, {
          is_competitor: !!this.factorForm.is_competitor
        });
        this.showFactorModal = false;
        await this.refreshBilling();
        this.childInfo = { ...this.childInfo, is_competitor: !!this.factorForm.is_competitor };
      } catch (e) { this.error = 'Hiba a tényezők mentésekor'; }
      this.savingFactor = false;
    },
    api() { return this.$BACKEND_HOST + '/api'; },
    async loadWallet() {
      try {
        const r = await axios.get(`${this.api()}/billing/wallet/${this.activeChildId}`);
        this.wallet = r.data.wallet;
      } catch (e) { this.error = 'Hiba a wallet betöltésekor'; }
    },
    async loadLedger() {
      try {
        const r = await axios.get(`${this.api()}/billing/ledger/${this.activeChildId}`);
        this.ledger = r.data.ledger || [];
      } catch (e) { /* ignore */ }
    },
    async loadPlans() {
      try {
        const r = await axios.get(`${this.api()}/billing/plans/${this.activeChildId}`);
        this.plans = (r.data.plans || []).map(p => ({ ...p, discounts: p.discounts || [] }));
      } catch (e) { this.error = 'Hiba a tervek betöltésekor'; }
    },
    async loadAbsences() {
      try {
        const r = await axios.get(`${this.api()}/billing/absences/${this.activeChildId}`);
        this.absences = r.data.absences || [];
      } catch (e) { /* ignore */ }
    },
    async loadCalculation() {
      try {
        const r = await axios.get(`${this.api()}/billing/calculate/${this.activeChildId}?month=${this.selectedMonth}`);
        const data = r.data || {};
        // The API returns null (not []) for discounts/absences when none exist.
        // Coerce to arrays so computed getters and v-for loops don't throw.
        if (data.plans) {
          data.plans.forEach(p => {
            if (!p.discounts) p.discounts = [];
            if (!p.absences) p.absences = [];
          });
        }
        this.calculation = data;
      } catch (e) { /* ignore */ }
    },
    async loadGroupPrices() {
      try {
        const r = await axios.get(`${this.api()}/billing/group-prices`);
        this.groupPrices = r.data.prices || [];
      } catch (e) { /* ignore */ }
    },
    // Refresh everything that affects the monthly bill so it updates on the fly
    // whenever a plan, discount, absence or wallet action changes.
    async refreshBilling() {
      await Promise.all([
        this.loadPlans(),
        this.loadAbsences(),
        this.loadLedger(),
        this.loadWallet(),
        this.loadGroupPrices(),
        this.loadCalculation()
      ]);
    },
    async loadMeta() {
      try {
        const [dr, gr] = await Promise.all([
          axios.get(`${this.api()}/billing/discount-rules`),
          axios.get(`${this.api()}/settings/groups/info`)
        ]);
        this.discountRules = dr.data.rules || [];
        this.groups = gr.data.groups || gr.data || [];
      } catch (e) { /* ignore */ }
    },
    // Plan modal
    openPlanModal() {
      this.editingPlan = null;
      this.planForm = { group_id: this.groups[0] ? this.groups[0].id : null, billing_mode: 'monthly', override_price: '', note: '', is_active: true };
      this.showPlanModal = true;
    },
    editPlan(plan) {
      this.editingPlan = plan;
      this.planForm = {
        id: plan.id,
        group_id: plan.group_id,
        billing_mode: plan.billing_mode,
        override_price: plan.override_price != null ? plan.override_price : '',
        note: plan.note,
        is_active: plan.is_active
      };
      this.showPlanModal = true;
    },
    async savePlan() {
      const payload = {
        child_id: this.activeChildId,
        group_id: parseInt(this.planForm.group_id, 10),
        billing_mode: this.planForm.billing_mode,
        override_price: this.planForm.override_price === '' ? null : parseFloat(this.planForm.override_price),
        note: this.planForm.note,
        is_active: this.planForm.is_active
      };
      try {
        if (this.editingPlan) {
          await axios.put(`${this.api()}/billing/plans`, { ...payload, id: this.editingPlan.id });
        } else {
          await axios.post(`${this.api()}/billing/plans`, payload);
        }
        this.showPlanModal = false;
        await this.refreshBilling();
      } catch (e) { this.error = 'Hiba a terv mentésekor'; }
    },
    async removePlan(id) {
      if (!confirm('Biztosan törlöd a tervet?')) return;
      await axios.delete(`${this.api()}/billing/plans/${id}`);
      await this.refreshBilling();
    },
    // Discounts
    openAddDiscount(plan) {
      this.discountTargetPlan = plan;
      this.discountForm = { rule_id: this.discountRules[0] ? this.discountRules[0].id : null, override_value: '' };
      this.showDiscountModal = true;
    },
    async savePlanDiscount() {
      const payload = {
        plan_id: this.discountTargetPlan.id,
        rule_id: parseInt(this.discountForm.rule_id, 10),
        override_value: this.discountForm.override_value === '' ? null : parseFloat(this.discountForm.override_value)
      };
      try {
        await axios.post(`${this.api()}/billing/plans/discounts`, payload);
        this.showDiscountModal = false;
        await this.refreshBilling();
      } catch (e) { this.error = 'Hiba a kedvezmény hozzáadásakor'; }
    },
    async removePlanDiscount(planId, ruleId) {
      await axios.delete(`${this.api()}/billing/plans/discounts`, { data: { plan_id: planId, rule_id: ruleId } });
      await this.refreshBilling();
    },
    // Absences
    openAbsenceModal() {
      this.absenceForm = { start_date: '', end_date: '', reduction_kind: 'proportional', reduction_value: 0.5, reason: '' };
      this.showAbsenceModal = true;
    },
    async saveAbsence() {
      const payload = { ...this.absenceForm, child_id: this.activeChildId };
      payload.reduction_value = parseFloat(payload.reduction_value);
      try {
        await axios.post(`${this.api()}/billing/absences`, payload);
        this.showAbsenceModal = false;
        await this.refreshBilling();
      } catch (e) { this.error = 'Hiba a távollét mentésekor'; }
    },
    async removeAbsence(id) {
      if (!confirm('Biztosan törlöd?')) return;
      await axios.delete(`${this.api()}/billing/absences/${id}`);
      await this.refreshBilling();
    },
    // Wallet
    openTopUp() { this.walletAction = { type: 'topup', title: 'Egyenleg feltöltése' }; this.walletForm = { amount: 0, note: '', method: 'készpénz' }; this.showWalletModal = true; },
    openCorrect() { this.walletAction = { type: 'correct', title: 'Korrekció (lehet negatív)' }; this.walletForm = { amount: 0, note: '' }; this.showWalletModal = true; },
    openRefund() { this.walletAction = { type: 'refund', title: 'Visszatérítés' }; this.walletForm = { amount: 0, note: '' }; this.showWalletModal = true; },
    async submitWalletAction() {
      const amount = parseFloat(this.walletForm.amount);
      let url = '';
      let payload = { child_id: this.activeChildId, amount, note: this.walletForm.note };
      if (this.walletAction.type === 'topup') { url = '/billing/wallet/topup'; payload.method = this.walletForm.method; }
      else if (this.walletAction.type === 'correct') { url = '/billing/wallet/correct'; }
      else { url = '/billing/wallet/refund'; }
      try {
        await axios.post(`${this.api()}${url}`, payload);
        this.showWalletModal = false;
        await this.refreshBilling();
      } catch (e) { this.error = 'Hiba a műveletnél'; }
    },
    // Formatting
    formatPrice(v) {
      const n = Math.round((v || 0));
      return n.toLocaleString('hu-HU') + ' Ft';
    },
    groupName(groupId) {
      const g = this.groups.find(x => x.id == groupId);
      return g ? g.name : ('#' + groupId);
    },
    formatDateTime(s) {
      if (!s) return '';
      return s.replace('T', ' ').slice(0, 16);
    },
    txnText(t) {
      return { topup: 'Feltöltés', deduction: 'Levonás', correction: 'Korrekció', refund: 'Visszatérítés' }[t] || t;
    },
    discountKindText(k) {
      return { percent: 'Százalék', fixed: 'Fix', sibling: 'Testvér' }[k] || k;
    },
    scopeText(s) {
      return { global: 'Általános', competitor: 'Versenyző', multi_group: 'Többcsoport', absence: 'Távollét', custom: 'Egyedi' }[s] || s || 'Egyedi';
    },
    absenceKindText(k) {
      return { proportional: 'Arányos', fixed: 'Fix', percent: 'Százalék' }[k] || k;
    }
  }
};
</script>

<style scoped>
.billing-container { max-width: 960px; margin: 0 auto; padding: 1.5rem; }
.billing-header h1 { margin-bottom: 0.2rem; }
.billing-header p { color: var(--color-text-muted); margin-top: 0; }
.wallet-card { display: flex; justify-content: space-between; align-items: center; padding: 1.2rem; border-radius: 12px; background: var(--card-bg); border: 1px solid var(--border); margin-bottom: 1rem; }
.wallet-card.in-debt { border-color: #e5484d; background: rgba(229,72,77,0.08); }
.wallet-left { display: flex; align-items: baseline; gap: 0.5rem; }
.wallet-label { color: var(--color-text-muted); }
.wallet-balance { font-size: 1.8rem; font-weight: 700; }
.wallet-actions { display: flex; gap: 0.5rem; }
.section { background: var(--card-bg); border: 1px solid var(--color-border); border-radius: 12px; padding: 1.2rem; margin-bottom: 1.2rem; }
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem; }
.plan-card, .absence-card { border: 1px solid var(--border); border-radius: 8px; padding: 0.7rem; margin-bottom: 0.6rem; }
.plan-head { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.plan-actions { margin-left: auto; display: flex; gap: 0.4rem; }
.mode-tag { background: var(--button-primary); color: #fff; border-radius: 6px; padding: 0.1rem 0.5rem; font-size: 0.8rem; }
.override-tag { background: #f5a623; color: #000; border-radius: 6px; padding: 0.1rem 0.5rem; font-size: 0.8rem; }
.inactive-tag { color: #e5484d; font-size: 0.85rem; }
.plan-discounts { display: flex; gap: 0.4rem; flex-wrap: wrap; margin-top: 0.5rem; }
.discount-chip { background: rgba(0,0,0,0.06); border-radius: 14px; padding: 0.2rem 0.6rem; font-size: 0.85rem; display: flex; align-items: center; gap: 0.3rem; }
.chip-x { border: none; background: transparent; cursor: pointer; color: #e5484d; font-weight: 700; }
.btn-add-discount { border: 1px dashed var(--border); background: transparent; border-radius: 14px; padding: 0.2rem 0.6rem; cursor: pointer; }
.calc-summary { border: 1px solid var(--border); border-radius: 8px; padding: 0.8rem; margin-bottom: 0.8rem; }
.calc-row { display: flex; justify-content: space-between; padding: 0.2rem 0; }
.calc-row.total { font-weight: 700; font-size: 1.1rem; border-top: 1px solid var(--border); margin-top: 0.3rem; padding-top: 0.4rem; }
.override-row { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; margin-top: 0.8rem; padding-top: 0.8rem; border-top: 1px dashed var(--border); }
.override-row label { font-size: 0.85rem; color: var(--color-text-muted); }
.override-row input { width: 140px; padding: 0.4rem 0.6rem; border-radius: 8px; border: 1px solid var(--border); background: var(--card-bg); color: var(--color-text); }
.calc-row.discount span:last-child { color: #2e9e5b; }
.calc-row.discount span:last-child { color: #2e9e5b; }
.calc-row.absence span:last-child { color: #f5a623; }
.plan-calc { border: 1px solid var(--border); border-radius: 8px; padding: 0.6rem; margin-bottom: 0.5rem; }
.plan-calc-head { display: flex; gap: 0.5rem; align-items: center; }
.plan-calc-head .plan-total { margin-left: auto; font-weight: 700; }
.plan-calc-body { display: flex; gap: 0.6rem; flex-wrap: wrap; font-size: 0.85rem; color: var(--color-text-muted); margin-top: 0.3rem; }
.applied-discount { color: #2e9e5b; }
.applied-absence { color: #f5a623; }
.group-price-table-wrap { overflow-x: auto; }
.group-price-table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
.group-price-table th, .group-price-table td { text-align: left; padding: 0.5rem 0.6rem; border-bottom: 1px solid var(--border); }
.group-price-table th { font-weight: 600; }
.group-name { font-weight: 600; }
.section-hint { padding: 0 1rem 0.8rem; color: var(--color-text-muted); font-size: 0.88rem; margin: 0; }
.factor-row { display: flex; gap: 0.6rem; flex-wrap: wrap; padding: 0 1rem 0.4rem; align-items: center; }
.factor-badge { border-radius: 16px; padding: 0.3rem 0.8rem; font-size: 0.85rem; background: rgba(0,0,0,0.06); color: var(--color-text-muted); border: 1px solid var(--border); }
.factor-badge.active { background: var(--button-primary); color: #fff; border-color: var(--button-primary); }
.factor-discount { color: #2e9e5b; font-weight: 600; font-size: 0.9rem; }
.catalog-tag { background: rgba(0,0,0,0.06); color: var(--color-text-muted); border-radius: 6px; padding: 0.1rem 0.5rem; font-size: 0.8rem; }
.discount-chip.scope-competitor { background: rgba(46,158,91,0.12); }
.discount-chip.scope-multi_group { background: rgba(245,166,35,0.14); }
.ledger-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
.ledger-table th, .ledger-table td { text-align: left; padding: 0.4rem; border-bottom: 1px solid var(--border); }
.neg { color: #e5484d; }
.pos { color: #2e9e5b; }
.empty-note { color: var(--color-text-muted); font-style: italic; }
.error-message { color: #e5484d; margin-bottom: 0.8rem; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0, 0, 0, 0.75); display: flex; align-items: center; justify-content: center; z-index: 1000; padding: 1rem; backdrop-filter: blur(4px); }
.modal-content { background: var(--card-bg); border-radius: 16px; box-shadow: var(--shadow-lg); max-width: 420px; width: 100%; max-height: 90vh; overflow-y: auto; border: 1px solid var(--border); }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1.5rem 2rem; border-bottom: 1px solid var(--border); background: var(--color-background-soft); }
.modal-header h3 { margin: 0; font-size: 1.25rem; font-weight: 600; color: var(--color-heading); }
.close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: var(--color-text-muted); width: 36px; height: 36px; display: flex; align-items: center; justify-content: center; border-radius: 8px; transition: all 0.2s ease; }
.close-btn:hover { background: var(--color-background); color: var(--color-text); }
.modal-body { padding: 2rem; }
.form-group { display: flex; flex-direction: column; gap: 0.5rem; }
.form-group label { font-weight: 600; color: var(--color-heading); font-size: 0.95rem; }
.checkbox-label { display: flex; align-items: center; gap: 0.5rem; cursor: pointer; }
.form-actions { display: flex; gap: 0.75rem; justify-content: flex-end; margin-top: 1.5rem; padding-top: 1rem; border-top: 1px solid var(--border); }
.btn-primary { background: var(--button-primary); color: #fff; border: none; padding: 0.5rem 0.9rem; border-radius: 8px; cursor: pointer; }
.btn-secondary { background: transparent; border: 1px solid var(--border); padding: 0.5rem 0.9rem; border-radius: 8px; cursor: pointer; color: var(--color-text); }
.btn-edit, .btn-delete { border: none; background: transparent; cursor: pointer; font-size: 0.85rem; }
.btn-edit { color: var(--button-primary); }
.btn-delete { color: #e5484d; }
</style>
